/*
 * Modular GLB viewer extracted from area-selector2.js
 */

class GLBViewer {
    constructor(canvasId, loadingId, progressSelectors = {}) {
      this.canvasId = canvasId;
      this.loadingId = loadingId;
      this.progressOverlayId = progressSelectors.overlayId || null;
      this.progressFillId = progressSelectors.fillId || null;
      this.progressTextId = progressSelectors.textId || null;
      this.progressTimer = null;
      this.lastProgressValue = 0;
      this.progressActive = false;
      this.progressStages = [];
      this.scene = null;
      this.camera = null;
      this.renderer = null;
      this.controls = null;
      this.model = null;
      this.animationId = null;
      this.defaultCameraPosition = { x: 50, y: 50, z: 50 };
      this.defaultCameraTarget = { x: 0, y: 0, z: 0 };
      this.resetButton = null;
      this.currentModelRadius = null;
      this.zoomAnimationFrame = null;
      this.boundWheelHandler = null;
      this.wheelListenerOptions = { passive: false, capture: true };
      this.engravingText = null;
      this.engravingPlane = null;
      this.engravingTexture = null;
      this.engravingOptions = null; // Engraving overlay disabled for now
      this.fontReady = null;
      this.currentLoadRequestId = null; // Track current load request to cancel stale ones
      
      this.isSupported = GLBViewer.isWebGLAvailable();
      if (!this.isSupported) {
        console.warn('[GLBViewer] WebGL is not available on this device; falling back to static preview.');
        return;
      }

      this.init();
    }

    static isWebGLAvailable() {
      try {
        if (!window.WebGLRenderingContext) return false;
        const canvas = document.createElement('canvas');
        return !!(canvas.getContext('webgl') || canvas.getContext('experimental-webgl'));
      } catch (error) {
        return false;
      }
    }

    async init() {
      try {
        
        // Always load Three.js using ES modules
        await this.loadThreeJS();

        // Verify Three.js components

        this.setupScene();
        this.setupCamera();
        this.setupRenderer();
        this.setupControls();
        this.setupLighting();
        this.setupEventListeners();
        this.createResetCameraButton();
        this.startAnimation();

      } catch (error) {
        console.error('❌ GLB Viewer initialization failed:', error);
        console.error('Error details:', error.message);
      }
    }

    async loadThreeJS() {
      try {
        // Check if Three.js is already loaded
        if (typeof window.THREE !== 'undefined' && window.THREE.GLTFLoader) {
          this.THREE = window.THREE;
          this.OrbitControls = window.THREE.OrbitControls;
          this.GLTFLoader = window.THREE.GLTFLoader;
          return true;
        }

        const preloadUrls = [
          {
            href: 'https://cdn.jsdelivr.net/npm/three@0.160.0/build/three.module.js',
            integrity: 'sha384-61S/Nu32S3E5+n+KpCOTb2eRYps6fVKm+9Gz1QBvSePFthb46f063Aa/qe/lykFZ'
          },
          {
            href: 'https://cdn.jsdelivr.net/npm/three@0.160.0/examples/jsm/controls/OrbitControls.js',
            integrity: 'sha384-qlO/ZugKPxAQUAvTlQoo0QECzxJIJySZmCF/DHdb2Xn/hHndFwX/vfUAC9Hbk6LP'
          },
          {
            href: 'https://cdn.jsdelivr.net/npm/three@0.160.0/examples/jsm/loaders/GLTFLoader.js',
            integrity: 'sha384-oxY9vSeh7Mrt5WmhN8xfWoDRnmoW9krEiRBY7vJKaK90jMUidM0fhbUwcblpBgTu'
          }
        ];

        preloadUrls.forEach(({ href, integrity }) => {
          if (!document.querySelector(`link[data-three-module="${href}"]`)) {
            const link = document.createElement('link');
            link.rel = 'modulepreload';
            link.href = href;
            link.crossOrigin = 'anonymous';
            link.integrity = integrity;
            link.setAttribute('data-three-module', href);
            document.head.appendChild(link);
          }
        });

        // Create import map if it doesn't exist
        if (!document.querySelector('script[type="importmap"]')) {
          const importMap = document.createElement('script');
          importMap.type = 'importmap';
          importMap.textContent = JSON.stringify({
            "imports": {
              // TODO: Add SRI hashes for security
              "three": "https://cdn.jsdelivr.net/npm/three@0.160.0/build/three.module.js",
              "three/addons/": "https://cdn.jsdelivr.net/npm/three@0.160.0/examples/jsm/"
            }
          });
          document.head.appendChild(importMap);
          
          // Wait for import map to be processed
          await new Promise(resolve => setTimeout(resolve, 50));
        }

        // Load Three.js components using ES module imports
        const [THREEModule, OrbitControlsModule, GLTFLoaderModule] = await Promise.all([
          import('three'),
          import('three/addons/controls/OrbitControls.js'),
          import('three/addons/loaders/GLTFLoader.js')
        ]);
        
        // Store them as instance properties (handle both default and named exports)
        this.THREE = THREEModule.default || THREEModule;
        this.OrbitControls = OrbitControlsModule.OrbitControls || OrbitControlsModule.default;
        this.GLTFLoader = GLTFLoaderModule.GLTFLoader || GLTFLoaderModule.default;
        
        return true;
      } catch (error) {
        console.error('❌ Failed to load Three.js ES modules:', error);
        throw error;
      }
    }

    async ensureEngravingFont() {
      if (!('fonts' in document) || typeof document.fonts.load !== 'function') {
        return;
      }
      if (!this.fontReady) {
        const samples = [
          '400 120px "Newake"',
          '400 120px "Newake Font"',
          '400 120px "NewakePreview"'
        ];
        this.fontReady = Promise.allSettled(samples.map((sample) => {
          try {
            return document.fonts.load(sample);
          } catch (err) {
            return Promise.resolve();
          }
        })).then(() => undefined);
      }
      try {
        await this.fontReady;
      } catch (err) {
        // Fallback to system font if loading fails
      }
    }

    setEngravingText(text) {
      this.engravingText = null;
    }

    clearEngravingOverlay() {
      if (this.engravingPlane && this.scene) {
        this.scene.remove(this.engravingPlane);
      }
      if (this.engravingPlane && this.engravingPlane.geometry) {
        this.engravingPlane.geometry.dispose();
      }
      if (this.engravingTexture) {
        this.engravingTexture.dispose();
      }
      this.engravingPlane = null;
      this.engravingTexture = null;
    }

    createEngravingCanvas(text) {
      const canvas = document.createElement('canvas');
      canvas.width = 1024;
      canvas.height = 512;
      return canvas;
    }

    async applyEngravingOverlay() {
      this.clearEngravingOverlay();
    }


    setupScene() {
      this.scene = new this.THREE.Scene();
      this.scene.background = new this.THREE.Color(0x3A3A3A);
    }

    setupCamera() {
      const canvas = document.getElementById(this.canvasId);
      this.camera = new this.THREE.PerspectiveCamera(
        45,
        canvas.clientWidth / canvas.clientHeight,
        0.1,
        1000
      );
      this.camera.position.set(50, 50, 50);
    }

    setupRenderer() {
      const canvas = document.getElementById(this.canvasId);
      this.renderer = new this.THREE.WebGLRenderer({ 
        canvas: canvas,
        antialias: true 
      });
      this.renderer.setSize(canvas.clientWidth, canvas.clientHeight);
      this.renderer.setPixelRatio(window.devicePixelRatio);
      if ('outputColorSpace' in this.renderer) {
        this.renderer.outputColorSpace = this.THREE.SRGBColorSpace;
      } else if ('outputEncoding' in this.renderer) {
        this.renderer.outputEncoding = this.THREE.sRGBEncoding;
      }
      this.renderer.toneMapping = this.THREE.ACESFilmicToneMapping;
      this.renderer.toneMappingExposure = 1.05;
      
      // Enable shadows
      this.renderer.shadowMap.enabled = true;
      this.renderer.shadowMap.type = this.THREE.PCFSoftShadowMap;
    }

    setupControls() {
      this.controls = new this.OrbitControls(this.camera, this.renderer.domElement);
      this.controls.enableDamping = true;
      this.controls.dampingFactor = 0.05;
      this.controls.enableZoom = true;
      this.controls.zoomSpeed = 0.55;
      this.controls.minDistance = 2;
      this.controls.maxDistance = 600;
      this.controls.enablePan = true;

      this.initSmoothZoom();
    }

    applyCameraConstraints({ radius, cameraDistance } = {}) {
      if (!this.controls || !this.camera) return;

      const safeRadius = Math.max(radius || 1, 0.01);
      const currentTarget = this.controls.target || new this.THREE.Vector3(0, 0, 0);
      const distance = Math.max(
        cameraDistance || this.camera.position.distanceTo(currentTarget),
        0.01
      );

      const minDistance = Math.max(safeRadius * 1.1, distance * 0.3);
      const maxDistance = Math.max(minDistance + safeRadius, safeRadius * 8, distance * 3);

      this.controls.minDistance = minDistance;
      this.controls.maxDistance = maxDistance;

      const computedZoomSpeed = distance / (safeRadius * 4.5);
      this.controls.zoomSpeed = Math.min(1.6, Math.max(0.5, computedZoomSpeed));

      this.camera.near = Math.max(0.1, minDistance * 0.1);
      this.camera.far = Math.max(1000, maxDistance * 1.5);
      this.camera.updateProjectionMatrix();

      this.controls.update();
    }

    initSmoothZoom() {
      if (!this.renderer || !this.renderer.domElement) return;

      if (this.boundWheelHandler) {
        this.renderer.domElement.removeEventListener('wheel', this.boundWheelHandler, this.wheelListenerOptions);
      }

      this.boundWheelHandler = (event) => this.handleWheelZoom(event);
      this.renderer.domElement.addEventListener('wheel', this.boundWheelHandler, this.wheelListenerOptions);
    }

    handleWheelZoom(event) {
      if (!this.controls || !this.camera) return;

      event.preventDefault();
      event.stopPropagation();

      const target = this.controls.target ? this.controls.target.clone() : new this.THREE.Vector3(0, 0, 0);
      const cameraPosition = this.camera.position.clone();
      const currentDistance = cameraPosition.distanceTo(target);
      if (!isFinite(currentDistance) || currentDistance <= 0) return;

      const deltaY = event.deltaY || 0;
      if (deltaY === 0) return;

      const normalizedIntensity = Math.min(1, Math.abs(deltaY) / 300);
      const direction = deltaY > 0 ? 1 : -1; // positive delta -> zoom out

      const minDistance = this.controls.minDistance || 0.1;
      const maxDistance = this.controls.maxDistance || 2000;
      const modelRadius = Math.max(this.currentModelRadius || currentDistance, 0.01);

      const baseStep = Math.max(modelRadius * 0.26, currentDistance * 0.11, 1.6);
      const dynamicMultiplier = 0.9 + normalizedIntensity * 0.8;
      let distanceDelta = baseStep * dynamicMultiplier * direction;

      // Avoid overshooting remaining travel space
      if (distanceDelta < 0) {
        const availableIn = currentDistance - minDistance;
        distanceDelta = -Math.min(Math.abs(distanceDelta), availableIn * 0.98);
      } else {
        const availableOut = maxDistance - currentDistance;
        distanceDelta = Math.min(distanceDelta, availableOut * 0.98);
      }

      const targetDistance = this.clampDistance(currentDistance + distanceDelta, minDistance, maxDistance);
      this.animateZoomTo(targetDistance, target);
    }

    clampDistance(value, min, max) {
      return Math.max(min, Math.min(max, value));
    }

    animateZoomTo(distance, target) {
      if (!this.camera || !this.controls) return;

      this.stopZoomAnimation();

      const initialTarget = target.clone();
      const startDistance = this.camera.position.distanceTo(initialTarget);
      const deltaDistance = distance - startDistance;
      if (Math.abs(deltaDistance) < 0.0001) return;

      const directionVector = this.camera.position.clone().sub(initialTarget);
      if (directionVector.lengthSq() === 0) return;

      const normalizedDirection = directionVector.normalize();
      const duration = 120;
      const startTime = performance.now();

      const animate = () => {
        const elapsed = performance.now() - startTime;
        const t = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - t, 3);
        const currentDistance = startDistance + deltaDistance * eased;

        const newPosition = normalizedDirection.clone().multiplyScalar(currentDistance).add(initialTarget);
        this.camera.position.copy(newPosition);
        this.controls.update();

        if (t < 1) {
          this.zoomAnimationFrame = requestAnimationFrame(animate);
        } else {
          this.zoomAnimationFrame = null;
          if (this.currentModelRadius) {
            this.applyCameraConstraints({ radius: this.currentModelRadius });
          }
        }
      };

      this.zoomAnimationFrame = requestAnimationFrame(animate);
    }

    stopZoomAnimation() {
      if (this.zoomAnimationFrame) {
        cancelAnimationFrame(this.zoomAnimationFrame);
        this.zoomAnimationFrame = null;
      }
    }

    setupLighting() {
      // Ambient light
      const ambientLight = new this.THREE.AmbientLight(0xffffff, 0.22);
      this.scene.add(ambientLight);

      // Key light
      const keyLight = new this.THREE.DirectionalLight(0xffffff, 1.4);
      keyLight.position.set(120, 95, 35);
      keyLight.castShadow = true;
      keyLight.shadow.mapSize.width = 2048;
      keyLight.shadow.mapSize.height = 2048;
      keyLight.shadow.camera.near = 0.5;
      keyLight.shadow.camera.far = 500;
      keyLight.shadow.camera.left = -150;
      keyLight.shadow.camera.right = 150;
      keyLight.shadow.camera.top = 150;
      keyLight.shadow.camera.bottom = -150;
      keyLight.shadow.bias = -0.0016;
      keyLight.shadow.normalBias = 0.045;
      this.scene.add(keyLight);

      // Fill light
      const fillLight = new this.THREE.DirectionalLight(0xffffff, 0.28);
      fillLight.position.set(-110, 70, -65);
      this.scene.add(fillLight);

      // Rim light
      const rimLight = new this.THREE.DirectionalLight(0xffffff, 0.38);
      rimLight.position.set(-30, 110, -140);
      this.scene.add(rimLight);

      // Accent spotlight to carve terrain detail
      const accentLight = new this.THREE.SpotLight(0xf7e0bc, 1.0, 0, Math.PI / 6, 0.35, 0.9);
      accentLight.position.set(65, 120, 25);
      accentLight.target.position.set(0, -5, -10);
      accentLight.castShadow = true;
      accentLight.shadow.mapSize.width = 2048;
      accentLight.shadow.mapSize.height = 2048;
      accentLight.shadow.camera.near = 5;
      accentLight.shadow.camera.far = 320;
      accentLight.shadow.bias = -0.0019;
      accentLight.shadow.normalBias = 0.05;
      this.scene.add(accentLight);
      this.scene.add(accentLight.target);

      // Secondary accent to brighten opposing slope
      const bounceLight = new this.THREE.SpotLight(0xf0d0a6, 0.32, 0, Math.PI / 5, 0.5, 0.65);
      bounceLight.position.set(-140, 85, 45);
      bounceLight.target.position.set(-20, 10, -10);
      bounceLight.castShadow = false;
      this.scene.add(bounceLight);
      this.scene.add(bounceLight.target);
    }

    setupEventListeners() {
      window.addEventListener('resize', () => this.onWindowResize());
    }

    createResetCameraButton() {
      // Create reset button
      this.resetButton = document.createElement('button');
      this.resetButton.className = 'glb-reset-camera-btn';
      this.resetButton.textContent = '🎯 Reset View';
      this.resetButton.title = 'Reset camera to default position';
      
      // Add click handler
      this.resetButton.addEventListener('click', () => {
        this.resetCamera();
      });

      // Add button to the canvas container
      const canvas = document.getElementById(this.canvasId);
      if (canvas && canvas.parentElement) {
        canvas.parentElement.appendChild(this.resetButton);
      }
    }

    resetCamera() {
      if (!this.camera || !this.controls) return;

      this.stopZoomAnimation();

      // Animate camera back to default position
      const startPosition = this.camera.position.clone();
      const startTarget = this.controls.target.clone();
      
      const endPosition = new this.THREE.Vector3(
        this.defaultCameraPosition.x,
        this.defaultCameraPosition.y,
        this.defaultCameraPosition.z
      );
      const endTarget = new this.THREE.Vector3(
        this.defaultCameraTarget.x,
        this.defaultCameraTarget.y,
        this.defaultCameraTarget.z
      );

      // Smooth animation over 800ms
      const duration = 800;
      const startTime = Date.now();

      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Use easing function for smooth animation
        const easeProgress = 1 - Math.pow(1 - progress, 3); // ease-out cubic
        
        // Interpolate camera position
        this.camera.position.lerpVectors(startPosition, endPosition, easeProgress);
        
        // Interpolate controls target
        this.controls.target.lerpVectors(startTarget, endTarget, easeProgress);
        
        // Update controls
        this.controls.update();
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        } else if (this.currentModelRadius) {
          this.applyCameraConstraints({ radius: this.currentModelRadius });
        }
      };
      
      animate();
    }

    onWindowResize() {
      const canvas = document.getElementById(this.canvasId);
      this.camera.aspect = canvas.clientWidth / canvas.clientHeight;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(canvas.clientWidth, canvas.clientHeight);
    }

    startAnimation() {
      const animate = () => {
        this.animationId = requestAnimationFrame(animate);
        this.controls.update();
        this.renderer.render(this.scene, this.camera);
      };
      animate();
    }

    async loadGLB(glbUrl) {
      if (!this.isSupported) {
        throw new Error('WebGL not supported on this device');
      }
      
      // Cancel any in-flight load requests by invalidating their request ID
      // This will cause any completing loads to be ignored
      const loadRequestId = Symbol('load-request');
      this.currentLoadRequestId = loadRequestId;
      
      try {
        this.showLoading(true);
        if (this.progressOverlayId) {
          if (!this.progressActive) {
            this.startProgress('Loading 3D model...');
          } else {
            this.updateProgress(Math.max(this.lastProgressValue, 55), 'Loading 3D model...');
          }
        }
        
        // Remove old model if exists
        this.clearEngravingOverlay();
        if (this.model) {
          this.scene.remove(this.model);
          this.model = null;
        }

        const loader = new this.GLTFLoader();
        
        let safeGlbUrl = glbUrl;
        if (typeof safeGlbUrl === 'string') {
          if (safeGlbUrl.startsWith('//')) {
            safeGlbUrl = `https:${safeGlbUrl}`;
          } else if (/^http:\/\//i.test(safeGlbUrl)) {
            safeGlbUrl = safeGlbUrl.replace(/^http:\/\//i, 'https://');
          }
        }

        return new Promise((resolve, reject) => {
          loader.load(
            safeGlbUrl,
            (gltf) => {
              // Check if this load request is still current (not cancelled by a newer request)
              if (this.currentLoadRequestId !== loadRequestId) {
                console.log('[GLBViewer] Ignoring stale GLB load result - newer request in progress');
                reject(new Error('Load request cancelled - newer request in progress'));
                return;
              }
              
              // Check if viewer is still valid
              if (!this.scene || !this.camera || !this.renderer) {
                console.warn('[GLBViewer] Viewer was destroyed, ignoring GLB load result');
                reject(new Error('Viewer destroyed'));
                return;
              }
              
              this.stopZoomAnimation();
              this.model = gltf.scene;

              // Base material used when the GLB does not define its own
              const beigeColor = new this.THREE.Color('#F8E9D6');
              if (typeof beigeColor.convertSRGBToLinear === 'function') {
                beigeColor.convertSRGBToLinear();
              }
              const beigeMaterial = new this.THREE.MeshStandardMaterial({
                color: beigeColor,
                roughness: 0.85,
                metalness: 0.0,
                flatShading: false,
                side: this.THREE.DoubleSide
              });

              const meshNodes = [];

              this.model.traverse((child) => {
                if (!child.isMesh) return;

                meshNodes.push(child);

                if (child.geometry && !child.geometry.attributes.normal) {
                  child.geometry.computeVertexNormals();
                }
              });

                const loadedNodeSummary = [];
                meshNodes.forEach((child) => {
                  const geometry = child.geometry;
                  const hasVertexColors = geometry && geometry.attributes && geometry.attributes.color;
                  const material = child.material;
                  const hasMaterial = material && material.isMaterial;
                  const hasTexture = hasMaterial && Boolean(
                    material.map ||
                    material.alphaMap ||
                    material.aoMap ||
                    material.bumpMap ||
                    material.displacementMap ||
                    material.emissiveMap ||
                    material.lightMap ||
                    material.metalnessMap ||
                    material.normalMap ||
                    material.roughnessMap
                  );

                  const parentName = child.parent && typeof child.parent.name === 'string' ? child.parent.name : '';
                  const nodeName = typeof child.name === 'string' ? child.name : '';
                  const isEngravingNode = (
                    nodeName.toLowerCase().includes('engraving') ||
                    parentName.toLowerCase().includes('engraving')
                  );
                  loadedNodeSummary.push({
                    node: nodeName || '(unnamed)',
                    parent: parentName || '(root)',
                    hasVertexColors,
                    hasTexture
                  });

                  if (isEngravingNode) {
                    if (hasVertexColors) {
                      const engravingMaterial = new this.THREE.MeshStandardMaterial({
                        vertexColors: true,
                        roughness: 0.6,
                        metalness: 0.05,
                        side: this.THREE.DoubleSide
                      });
                      engravingMaterial.needsUpdate = true;
                      child.material = engravingMaterial;
                    } else {
                      const engravingColor = new this.THREE.Color('#C5AA83');
                      if (typeof engravingColor.convertSRGBToLinear === 'function') {
                        engravingColor.convertSRGBToLinear();
                      }
                      const engravingMaterial = new this.THREE.MeshStandardMaterial({
                        color: engravingColor,
                        roughness: 0.6,
                        metalness: 0.05,
                        side: this.THREE.DoubleSide
                      });
                      engravingMaterial.vertexColors = false;
                      engravingMaterial.needsUpdate = true;
                      child.material = engravingMaterial;
                    }
                    child.castShadow = true;
                    child.receiveShadow = true;
                    return;
                  }

                  const overrideMaterial = beigeMaterial.clone();
                  overrideMaterial.vertexColors = false;
                  overrideMaterial.color.copy(beigeColor);
                  overrideMaterial.side = this.THREE.DoubleSide;
                  overrideMaterial.needsUpdate = true;

                  if (hasVertexColors && geometry.deleteAttribute) {
                    geometry.deleteAttribute('color');
                  }

                  if (hasTexture && hasMaterial) {
                    const texturedMaterial = material.clone ? material.clone() : material;
                    texturedMaterial.side = this.THREE.DoubleSide;
                    texturedMaterial.vertexColors = false;
                    texturedMaterial.color.copy(beigeColor);
                    texturedMaterial.needsUpdate = true;
                    child.material = texturedMaterial;
                  } else {
                    child.material = overrideMaterial;
                  }

                  child.castShadow = true;
                  child.receiveShadow = true;
                });
                if (loadedNodeSummary.length) {
                  console.info('[Landmark][GLBViewer] Mesh nodes loaded:', loadedNodeSummary);
                } else {
                  console.warn('[Landmark][GLBViewer] No mesh nodes detected in GLB scene.');
                }

              // Fix orientation
              this.model.rotation.x = -Math.PI / 2;

              // Center model
              const box = new this.THREE.Box3().setFromObject(this.model);
              const center = box.getCenter(new this.THREE.Vector3());
              this.model.position.sub(center);
              const boundingSphere = new this.THREE.Sphere();
              box.getBoundingSphere(boundingSphere);
              const modelRadius = Math.max(boundingSphere.radius, 0.01);
              this.currentModelRadius = modelRadius;

              if (this.scene) {
                this.scene.add(this.model);
              } else {
                console.error('❌ Scene not initialized when trying to add model');
                reject(new Error('Scene not initialized'));
                return;
              }

              // Add ground plane
              const size = box.getSize(new this.THREE.Vector3());
              const minY = box.min.y - center.y;
              
              const oldGround = this.scene.getObjectByName('groundPlane');
              if (oldGround) this.scene.remove(oldGround);
              
              const groundSize = Math.max(size.x, size.z) * 2;
              const groundGeometry = new this.THREE.PlaneGeometry(groundSize, groundSize);
              const groundMaterial = new this.THREE.ShadowMaterial({ opacity: 0.4 });
              const ground = new this.THREE.Mesh(groundGeometry, groundMaterial);
              ground.rotation.x = -Math.PI / 2;
              ground.position.y = minY - 0.1;
              ground.receiveShadow = true;
              ground.name = 'groundPlane';
              if (this.scene) {
                this.scene.add(ground);
              }

              this.applyEngravingOverlay();

              // Position camera
              const maxDim = Math.max(size.x, size.y, size.z);
              const fov = this.camera.fov * (Math.PI / 180);
              let cameraZ = Math.abs(maxDim / Math.sin(fov / 2)) * 0.5;

              const optimalPosition = {
                x: cameraZ * 0.5,
                y: cameraZ * 0.5,
                z: cameraZ
              };

              this.camera.position.set(optimalPosition.x, optimalPosition.y, optimalPosition.z);
              this.camera.lookAt(0, 0, 0);
              this.controls.target.set(0, 0, 0);
              const cameraDistance = this.camera.position.distanceTo(this.controls.target);
              this.applyCameraConstraints({ radius: modelRadius, cameraDistance });
              if (typeof this.controls.saveState === 'function') {
                this.controls.saveState();
              }

              // Update default camera position for reset functionality
              this.defaultCameraPosition = { ...optimalPosition };
              this.defaultCameraTarget = { x: 0, y: 0, z: 0 };

              this.applyEngravingOverlay();
              
              // Final check before resolving
              if (this.currentLoadRequestId !== loadRequestId) {
                console.log('[GLBViewer] Request cancelled after model setup, ignoring');
                reject(new Error('Load request cancelled - newer request in progress'));
                return;
              }
              
              if (!this.scene || !this.camera || !this.renderer) {
                console.warn('[GLBViewer] Viewer destroyed after model setup, ignoring');
                reject(new Error('Viewer destroyed'));
                return;
              }
              
              this.showLoading(false);
              this.completeProgress('Preview ready!');
              resolve(gltf);
            },
            (progress) => {
              // Only update progress if this is still the current request
              if (this.currentLoadRequestId !== loadRequestId) {
                return;
              }
              
              if (progress && progress.total) {
                const percent = progress.total > 0
                  ? (progress.loaded / progress.total) * 100
                  : 0;
                const scaled = 55 + (Math.min(percent, 100) * 0.4); // keep below 95%
                this.updateProgress(Math.min(scaled, 95));
              } else {
                this.updateProgress(Math.min(this.lastProgressValue + 3, 90));
              }
            },
            (error) => {
              // Only handle error if this is still the current request
              if (this.currentLoadRequestId !== loadRequestId) {
                console.log('[GLBViewer] Ignoring error from cancelled load request');
                return;
              }
              
              console.error('❌ GLB load error:', error);
              this.showLoading(false);
              this.failProgress('Preview failed to load');
              reject(error);
            }
          );
        });

      } catch (error) {
        // Only handle error if this is still the current request
        if (this.currentLoadRequestId !== loadRequestId) {
          console.log('[GLBViewer] Ignoring error from cancelled load request in catch block');
          return;
        }
        
        console.error('❌ Error loading GLB:', error);
        this.showLoading(false);
        this.failProgress('Preview failed to load');
        throw error;
      }
    }

    showLoading(show) {
      if (!this.isSupported) return;
      const loadingEl = document.getElementById(this.loadingId);
      const canvas = document.getElementById(this.canvasId);
      
      if (loadingEl && canvas) {
        loadingEl.style.display = show ? 'flex' : 'none';
        canvas.style.display = show ? 'none' : 'block';
      }
      
    }

    startProgress(text = 'Preparing 3D preview...') {
      if (!this.isSupported) return;
      if (!this.progressOverlayId) return;
      this.clearProgressTimer();
      this.clearProgressStages();
      
      
      const overlay = document.getElementById(this.progressOverlayId);
      if (overlay) {
        overlay.classList.add('active');
      }
      
      // Hide the old spinning loader completely
      const loadingEl = document.getElementById(this.loadingId);
      if (loadingEl) {
        loadingEl.style.display = 'none';
      }
      this.progressActive = true;
      this.lastProgressValue = 0;
      this.updateProgress(8, text);

      // staged boosts to keep user engaged while backend warms up
      const stagedUpdates = [
        { delay: 400, value: 15, message: 'Sending area for processing...' },
        { delay: 1100, value: 32, message: 'Fetching terrain data...' },
        { delay: 1900, value: 48, message: 'Generating 3D contours...' },
        { delay: 2600, value: 60, message: 'Preparing model preview...' }
      ];
      stagedUpdates.forEach(stage => {
        const timer = setTimeout(() => {
          if (!this.progressActive) return;
          if (this.lastProgressValue >= stage.value) return;
          this.updateProgress(stage.value, stage.message);
        }, stage.delay);
        this.progressStages.push(timer);
      });

      this.progressTimer = setInterval(() => {
        if (this.lastProgressValue < 85) {
          this.updateProgress(this.lastProgressValue + 3);
        }
      }, 450);
    }

    updateProgress(value, text) {
      if (!this.isSupported) return;
      if (!this.progressOverlayId) return;
      const clampedValue = Math.max(0, Math.min(100, value));
      this.lastProgressValue = clampedValue;
      
      // Simple text-based progress - no visual bars
      if (text) {
        const progressText = `${text} (${Math.round(clampedValue)}%)`;
        this.updateProgressText(progressText);
      }
    }

    updateProgressText(text) {
      if (!this.isSupported) return;
      if (!this.progressTextId) return;
      const textEl = document.getElementById(this.progressTextId);
      if (textEl) {
        textEl.textContent = text;
      }
    }

    completeProgress(text = 'Preview ready!') {
      if (!this.isSupported) return;
      if (!this.progressOverlayId) return;
      this.clearProgressTimer();
      this.clearProgressStages();
      this.updateProgress(100, text);
      setTimeout(() => this.hideProgressOverlay(), 350);
    }

    failProgress(text = 'Preview unavailable') {
      if (!this.isSupported) return;
      if (!this.progressOverlayId) return;
      this.clearProgressTimer();
      this.clearProgressStages();
      this.updateProgress(100, text);
      setTimeout(() => this.hideProgressOverlay(), 1200);
    }

    hideProgressOverlay() {
      if (!this.isSupported) return;
      if (!this.progressOverlayId) return;
      const overlay = document.getElementById(this.progressOverlayId);
      if (overlay) {
        overlay.classList.remove('active');
      }
      if (this.progressFillId) {
        const fill = document.getElementById(this.progressFillId);
        if (fill) {
          fill.style.transform = 'scaleX(0)';
          fill.dataset.progress = 0;
        }
      }
      const loadingEl = document.getElementById(this.loadingId);
      if (loadingEl) {
        const prevOpacity = loadingEl.dataset.prevOpacity;
        if (prevOpacity !== undefined) {
          loadingEl.style.opacity = prevOpacity;
          delete loadingEl.dataset.prevOpacity;
        } else {
          loadingEl.style.opacity = '';
        }
      }
      this.lastProgressValue = 0;
      this.progressActive = false;
    }

    clearProgressTimer() {
      if (this.progressTimer) {
        clearInterval(this.progressTimer);
        this.progressTimer = null;
      }
    }

    clearProgressStages() {
      if (this.progressStages && this.progressStages.length) {
        this.progressStages.forEach(timerId => clearTimeout(timerId));
      }
      this.progressStages = [];
    }

    destroy() {
      if (!this.isSupported) {
        return;
      }
      
      // Cancel any in-flight GLB loads by invalidating the request ID
      this.currentLoadRequestId = null;
      
      this.stopZoomAnimation();
      if (this.animationId) {
        cancelAnimationFrame(this.animationId);
      }
      if (this.renderer) {
        this.renderer.dispose();
      }
      if (this.resetButton && this.resetButton.parentElement) {
        this.resetButton.parentElement.removeChild(this.resetButton);
        this.resetButton = null;
      }
      this.clearProgressTimer();
      this.clearProgressStages();
      this.hideProgressOverlay();
      if (this.boundWheelHandler && this.renderer && this.renderer.domElement) {
        this.renderer.domElement.removeEventListener('wheel', this.boundWheelHandler, this.wheelListenerOptions);
        this.boundWheelHandler = null;
      }
    }
  }

  

if (typeof window !== 'undefined') {
  window.Landmark = window.Landmark || {};
  window.Landmark.GLBViewer = window.Landmark.GLBViewer || GLBViewer;
}
